import {
  Box,
  Button,
  CardMedia,
  Container,
  makeStyles,
  Typography,
} from "@material-ui/core";
import moment from "moment";
import React, { useEffect, useState } from "react";
import EmployeeSignButton from "../../component/EmployeeSignButton";
import CompanyHeader from "../CompanyHeader";
import ModalContainer from "../../component/ModalContainer";

type DocumentTemplateProps = {
  brand: string;
  docContent: any;
  signature: string;
  signatureDate: Date;
  confirmCallback?: (signature: string) => void;
  readonly?: boolean;
};

const DocumentTitle = (documentName: string, header?: string) => {
  const classes = useStyles();
  return (
    <>
      {documentName === "reference_authorization" && (
        <div className={classes.title}>
          <Typography variant="h5" style={{ textAlign: "center" }}>
            Reference Check Authorization
            <br />
            <br />
            授權書
          </Typography>
        </div>
      )}
      {documentName === "personal_information" && (
        <>
          <Typography variant="h5" className={classes.title}>
            {header || "個人資料(指紋及面部圖像)收集同意書"}
          </Typography>
        </>
      )}
      {documentName === "handbook" && (
        <>
          <Typography variant="h5" className={classes.title}>
            {header || "《員工手冊》簽收紀錄"}
          </Typography>
        </>
      )}
    </>
  );
};

const DocumentTemplate = (props: DocumentTemplateProps) => {
  const classes = useStyles();
  const {
    brand,
    docContent,
    signature,
    signatureDate,
    confirmCallback,
    readonly,
  } = props;
  const [openSignModal, setOpenSignModal] = useState(false);
  const [signatureUrl, setSignatureUrl] = useState<string | null>(
    signature?.replace('""', "")?.replace(/\"/g, "") || null
  );

  // Callback to handle signature capture from ModalContainer
  const handleSignature = (pngDataUrl: string, svg: string) => {
    setSignatureUrl(pngDataUrl);
    setOpenSignModal(false);
  };

  return (
    <Container className={classes.container}>
      <div className={`${classes.mainBody} mainBody`}>
        <Box >
          <CompanyHeader brand={brand} isHideTitle={true} />
          {DocumentTitle(
            docContent?.documentName,
            docContent?.chinese?.header?.text
          )}

          {docContent?.english && (
            <>
              <Typography className={classes.titleContent}>
                {Object.keys(docContent?.english).map((key: any) => {
                  const item = docContent?.english[key];
                  let className = "";
                  if (key === "staffName" || key === "hkidValue") {
                    className = classes.boldUnderline;
                    item.text = item.text;
                  }
                  if (key === "noticeDate") {
                    item.text = moment(item.text).format("YYYY-MM-DD");
                  }

                  return (
                    <>
                      <span className={className}>{item?.text}</span>
                      {item.lineBreak && (
                        <>
                          <br />
                          <br />
                        </>
                      )}{" "}
                    </>
                  );
                })}
              </Typography>
              <div style={{ padding: "30px 0" }} />
            </>
          )}

          {docContent?.chinese && (
            <Typography className={classes.titleContent}>
              {Object.keys(docContent?.chinese)
                .filter((key: any) => !["header"].includes(key))
                .map((key: any) => {
                  const item = docContent?.chinese[key];
                  let className = "";
                  if (key === "staffName" || key === "hkidValue") {
                    className = classes.boldUnderline;
                  }
                  if (key === "noticeDate") {
                    item.text = moment(item.text).format("YYYY-MM-DD");
                  }

                  return (
                    <>
                      <span className={className}>{item?.text}</span>
                      {item.lineBreak && (
                        <>
                          <br />
                          <br />
                        </>
                      )}{" "}
                    </>
                  );
                })}
            </Typography>
          )}
        </Box>

        {!signatureUrl && (
          <Box display="flex" justifyContent="center" mt={4}>
            <EmployeeSignButton
              onClick={() => {
                setOpenSignModal?.(true);
              }}
              signature={signatureUrl}
            />
          </Box>
        )}

        {signatureUrl && (
          <Box >
            <Box className={classes.signatureContainer}>
              <Box
                className={classes.signatureBox}
                style={{ paddingRight: "30px" }}
              >
                <div
                  style={{
                    width: "100%",
                    height: "125px",
                    textAlign: "center",
                  }}
                >
                  <img
                    src={signatureUrl}
                    alt=""
                    style={{ maxHeight: "125px", maxWidth: "100%" }}
                  />
                </div>
                <hr className={classes.line} />
                <Typography className={classes.content}>
                  Signature 簽署
                </Typography>
              </Box>
              <Box
                className={classes.signatureBox}
                style={{ paddingLeft: "30px" }}
              >
                <Typography className={classes.dateText}>
                  {signatureDate
                    ? moment(signatureDate).format("YYYY-MM-DD")
                    : moment().format("YYYY-MM-DD")}
                </Typography>
                <hr className={classes.line} />
                <Typography className={classes.content}>Date 日期</Typography>
              </Box>
            </Box>
            {!readonly && (
              <Box className={classes.buttonContainer}>
                <Button
                  startIcon={
                    <CardMedia
                      component="img"
                      image={require("../../icons/edit.png")}
                      alt=""
                      className={classes.media}
                    />
                  }
                  variant="contained"
                  color="primary"
                  onClick={() => setOpenSignModal?.(true)}
                  className={classes.editButton}
                >
                  修改簽名
                </Button>

                <Button
                  disabled={false}
                  variant="contained"
                  color="primary"
                  onClick={() => {
                    confirmCallback?.(signatureUrl);
                  }}
                  className={classes.button}
                >
                  完成
                </Button>
              </Box>
            )}
          </Box>
        )}
      </div>
      <ModalContainer
        open={openSignModal}
        setOpen={setOpenSignModal}
        type="sign"
        SignTrigger={handleSignature}
      />
    </Container>
  );
};

export default DocumentTemplate;

const useStyles = makeStyles((theme) => ({
  title: {
    fontWeight: 400,
    display: "flex",
    justifyContent: "center",
    padding: "20px 0px 70px 0px",
  },
  titleContent: {
    fontWeight: 400,
    fontSize: "20px",
    fontFamily: "Noto Sans",
    lineHeight: "150%",
    letterSpacing: "0%",
    "@media print": {
      fontSize: "16px",
      lineHeight: "2",
    },
  },
  container: {
    // display: "flex",
    padding: "24px",
    borderRadius: "32px",
    "@media print": {
      display: "flex",
      flexDirection: "column",
      minHeight: "90vh", // Full page height
      pageBreakAfter: "always",
    },
  },
  mainBody: {
    padding: "36px 90px",
    backgroundColor: "#FFFF",
    borderRadius: "32px",
    color: "#505050",
    fontFamily: "Noto Sans",
    fontSize: "20px",
    "@media print": {
      display: "flex",
      flexDirection: "column",
      justifyContent: "space-between",
      minHeight: "88vh",
      maxHeight: "88vh",
    },
  },
  boldUnderline: {
    fontWeight: "bold",
    textDecoration: "underline",
  },
  separator: { padding: "1rem 0" },
  line: {
    margin: "1.5rem 0",
    width: "100%",
    borderTop: "2px solid #505050",
    borderBottom: "0px",
    borderLeft: "0px",
    borderRight: "0px",
  },
  signatureDate: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    flexDirection: "column",
  },
  content: {
    color: "#505050",
    fontSize: "1.3rem",
    textAlign: "center",
  },
  contentDate: {
    color: "#505050",
    fontSize: "1.3rem",
    display: "flex",
    alignItems: "flex-end",
    height: "5rem",
  },
  portion: {
    display: "flex",
    justifyContent: "space-between",
    padding: "3rem 0px 1rem 0px",
    alignItems: "center",
  },
  media: {
    width: "20px",
    height: "20px",
    margin: "5px",
    objectFit: "contain",
  },
  button: {
    margin: theme.spacing(1, 0),
    width: "10rem",
    alignSelf: "center",
  },
  signatureImg: {
    width: "13rem",
    height: "5rem",
  },
  leftButton: {
    backgroundColor: "#908DF2",
    margin: theme.spacing(1, 0),
    width: "10rem",
  },
  blueTitle: {
    color: "#454C96",
    paddingTop: "2rem",
    fontSize: "20px",
  },
  signatureContainer: {
    display: "flex",
    justifyContent: "space-between",
    padding: "50px 0 0 0",
  },
  signatureBox: {
    width: "50%",
  },
  dateText: {
    height: "130px",
    color: "#505050",
    fontSize: "20px",
    textAlign: "center",
    display: "flex",
    alignItems: "flex-end",
    justifyContent: "center",
  },
  buttonContainer: {
    display: "flex",
    justifyContent: "space-between",

    padding: "20px 80px 0 100px",
  },
  editButton: {
    margin: theme.spacing(1, 0),
    backgroundColor: "#908DF2",
    height: "3rem",
  },
  "@global": {
    "@media print, .print-mode": {
      // Target both print and print-mode class
      ".mainBody": {
        padding: "40px 60px !important",
        fontSize: "14px !important",
        lineHeight: "1.8 !important",
        height: "297mm !important",
        display: "flex !important",
        flexDirection: "column !important",
      },
      ".titleContent": {
        fontSize: "14px !important",
        lineHeight: "2.5 !important",
        marginTop: "40px !important",
      },
    },
    ".pdfStyle.mainBody": {
      display: "flex !important",
      flexDirection: "column !important",
      minHeight: "26cm !important",
      justifyContent: "space-between !important",
    }
  },
}));
